// ============================================================================
// CHANGO V5.0 — HTTP Client with refresh-token rotation
// ============================================================================
// Single fetch wrapper used by every page / context.
//
// Features:
//   - Always sends credentials: 'include' so the backend can set the
//     access_token cookie on .chango-tech.com subdomains (§20 坑 #11).
//   - Adds an idempotency token to every write for the server's dedupe layer.
//   - On 401 with code='TOKEN_EXPIRED', transparently calls /api/auth/refresh
//     exactly once and retries the original request. If that refresh also
//     fails, bubbles a 'AUTH_REQUIRED' rejection the AuthContext can catch.
//   - Normalises backend error shape { code, message, details } into a thrown
//     Error with those fields as enumerable properties.
// ============================================================================

const BASE = import.meta.env.VITE_API_BASE || '';

// Endpoints that must not trigger the refresh-and-retry dance — attempting to
// refresh inside the refresh / logout flows would cause infinite recursion.
const REFRESH_BLACKLIST = new Set([
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/login',
  '/api/auth/register'
]);

function newIdempotencyKey() {
  // 128 bits of entropy is overkill but cheap.
  if (crypto?.randomUUID) return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export class HttpError extends Error {
  constructor({ status, code, message, details }) {
    super(message || code || `HTTP ${status}`);
    this.name = 'HttpError';
    this.status = status;
    this.code = code || `HTTP_${status}`;
    this.details = details ?? null;
  }
}

async function parseResponse(res) {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { return await res.json(); } catch { return null; }
  }
  return null;
}

let _refreshInFlight = null;

// ---- §11.10 re-consent interceptor -----------------------------------------
// When the server returns 409 AGREEMENT_VERSION_MISMATCH on a write, a globally
// registered handler (installed by <ReconsentGate /> in AppShell) opens a
// blocking modal, collects the user's re-consent, POSTs
// /api/legal/consent for each stale agreement type, and resolves. The client
// then transparently retries the original request exactly once.
//
// The handler is invoked with the structured `details` payload:
//   { mismatch: 'tos' | 'privacy' | 'tos,privacy', required: {...}, hint }
// It must return a Promise; any rejection (user cancels, consent POST fails)
// bubbles the original 409 unchanged.
let _reconsentHandler = null;
let _reconsentInFlight = null;

export function setReconsentHandler(fn) {
  _reconsentHandler = typeof fn === 'function' ? fn : null;
}

async function runReconsent(details) {
  if (!_reconsentHandler) {
    throw new Error('no reconsent handler registered');
  }
  // Coalesce parallel failures onto a single modal lifecycle.
  if (_reconsentInFlight) return _reconsentInFlight;
  _reconsentInFlight = Promise.resolve()
    .then(() => _reconsentHandler(details))
    .finally(() => {
      setTimeout(() => { _reconsentInFlight = null; }, 0);
    });
  return _reconsentInFlight;
}

async function doRefresh() {
  // Coalesce concurrent refresh attempts into a single network call.
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = fetch(`${BASE}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}'
  }).then(async (r) => {
    const body = await parseResponse(r);
    if (!r.ok) {
      const err = new HttpError({
        status: r.status,
        code: body?.code || 'AUTH_REQUIRED',
        message: body?.message || 'refresh failed',
        details: body?.details
      });
      throw err;
    }
    return body || {};
  }).finally(() => {
    // Always clear the in-flight slot so subsequent lifetimes can refresh.
    setTimeout(() => { _refreshInFlight = null; }, 0);
  });
  return _refreshInFlight;
}

async function raw(method, path, { body, headers, signal, isRetry } = {}) {
  const init = {
    method,
    credentials: 'include',
    signal,
    headers: {
      'Accept': 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(method !== 'GET' && method !== 'HEAD'
        ? { 'X-Idempotency-Key': newIdempotencyKey() }
        : {}),
      ...(headers || {})
    },
    body: body !== undefined ? JSON.stringify(body) : undefined
  };

  const res = await fetch(`${BASE}${path}`, init);
  const payload = await parseResponse(res);

  if (res.ok) return payload;

  const code = payload?.code || `HTTP_${res.status}`;
  const shouldRetry =
    res.status === 401 &&
    (code === 'TOKEN_EXPIRED' || code === 'UNAUTHORIZED') &&
    !isRetry &&
    !REFRESH_BLACKLIST.has(path);

  if (shouldRetry) {
    try {
      await doRefresh();
      return raw(method, path, { body, headers, signal, isRetry: true });
    } catch (refreshErr) {
      // Refresh itself failed — bubble a clean AUTH_REQUIRED for the context.
      throw new HttpError({
        status: 401,
        code: 'AUTH_REQUIRED',
        message: refreshErr?.message || 'session expired'
      });
    }
  }

  // §11.10 — re-consent gate. If the workspace has agreed to an old version,
  // the middleware returns 409 AGREEMENT_VERSION_MISMATCH. We hand off to the
  // globally registered handler (the <ReconsentGate /> modal); once it
  // resolves, retry the original write exactly once.
  const isReconsent =
    res.status === 409 &&
    code === 'AGREEMENT_VERSION_MISMATCH' &&
    !isRetry &&
    _reconsentHandler;

  if (isReconsent) {
    try {
      await runReconsent(payload?.details || null);
      return raw(method, path, { body, headers, signal, isRetry: true });
    } catch (_) {
      // User cancelled / consent POST failed — propagate original 409 so the
      // caller can show its own error UI. Do not wrap; keep status + code.
    }
  }

  throw new HttpError({
    status: res.status,
    code,
    message: payload?.message || res.statusText,
    details: payload?.details
  });
}

export const api = {
  get:    (p, opts) => raw('GET', p, opts),
  post:   (p, body, opts) => raw('POST', p, { ...(opts || {}), body }),
  patch:  (p, body, opts) => raw('PATCH', p, { ...(opts || {}), body }),
  put:    (p, body, opts) => raw('PUT', p, { ...(opts || {}), body }),
  delete: (p, opts) => raw('DELETE', p, opts),
  // multipart helper: callers build the FormData themselves
  upload: async (p, formData, opts = {}) => {
    const res = await fetch(`${BASE}${p}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: {
        'X-Idempotency-Key': newIdempotencyKey(),
        ...(opts.headers || {})
      },
      signal: opts.signal
    });
    const payload = await parseResponse(res);
    if (!res.ok) {
      throw new HttpError({
        status: res.status,
        code: payload?.code || `HTTP_${res.status}`,
        message: payload?.message || res.statusText,
        details: payload?.details ?? payload
      });
    }
    return payload;
  }
};

export default api;
